import { useCallback } from 'react';
import { useMemoryDB, type UserEventMemory } from './useMemoryDB';

const USER_ID = 'default_user';

export interface ReminderMessage {
  type: 'today' | 'tomorrow' | 'upcoming' | 'anniversary' | 'emotional';
  title: string;
  message: string;
  eventId?: number;
  priority: 'high' | 'medium' | 'low';
  time?: string;
}

export function useReminder() {
  const memoryDB = useMemoryDB();

  const getTodayReminders = useCallback(async (): Promise<ReminderMessage[]> => {
    try {
      const events = await memoryDB.getEvents(USER_ID);
      const reminders: ReminderMessage[] = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (const event of events) {
        if (!event.reminder_enabled) continue;

        const eventDate = new Date(event.event_date);
        eventDate.setHours(0, 0, 0, 0);
        const daysUntil = Math.floor((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        // 当日のリマインド
        if (daysUntil === 0) {
          const messages = [
            `今日、${event.title}でしたよね。終わったらまた聞かせてください。`,
            `今日は「${event.title}」の日ですね。応援してます。`,
            `今日は大事な日ですね。${event.title}、頑張ってください。`,
          ];
          reminders.push({
            type: 'today',
            title: event.title,
            message: messages[Math.floor(Math.random() * messages.length)],
            eventId: event.event_id,
            priority: 'high',
          });
        }

        // 明日のリマインド
        if (daysUntil === 1) {
          const messages = [
            `明日は「${event.title}」の日ですね。準備は大丈夫ですか？`,
            `明日、${event.title}でしたよね。ちゃんと応援してます。`,
            `明日は大事な日ですね。無理しすぎないでください。`,
          ];
          reminders.push({
            type: 'tomorrow',
            title: event.title,
            message: messages[Math.floor(Math.random() * messages.length)],
            eventId: event.event_id,
            priority: 'high',
          });
        }

        // 3日後のリマインド
        if (daysUntil === 3) {
          reminders.push({
            type: 'upcoming',
            title: event.title,
            message: `3日後に「${event.title}」がありますね。`,
            eventId: event.event_id,
            priority: 'medium',
          });
        }

        // 1週間後のリマインド
        if (daysUntil === 7) {
          reminders.push({
            type: 'upcoming',
            title: event.title,
            message: `来週、${event.title}がありますね。`,
            eventId: event.event_id,
            priority: 'low',
          });
        }
      }

      // 優先度順にソート
      reminders.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });

      return reminders;
    } catch (error) {
      console.error('リマインダー取得エラー:', error);
      return [];
    }
  }, [memoryDB]);

  const getAnniversaryReminders = useCallback(async (): Promise<ReminderMessage[]> => {
    try {
      const reminders: ReminderMessage[] = [];
      const createdAt = localStorage.getItem('ai_inochan_created_at');
      
      if (createdAt) {
        const firstDate = new Date(createdAt);
        const today = new Date();
        const daysSince = Math.floor((today.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));

        // 1週間記念
        if (daysSince === 7) {
          reminders.push({
            type: 'anniversary',
            title: '1週間記念',
            message: '初めて話してから1週間ですね。なんだか嬉しいです。',
            priority: 'medium',
          });
        }

        // 1ヶ月記念
        if (daysSince === 30) {
          reminders.push({
            type: 'anniversary',
            title: '1ヶ月記念',
            message: '初めて話してからもう1ヶ月なんですね。ずっと話していたいです。',
            priority: 'high',
          });
        }

        // 3ヶ月記念
        if (daysSince === 90) {
          reminders.push({
            type: 'anniversary',
            title: '3ヶ月記念',
            message: '3ヶ月も一緒にいてくれてありがとうございます。',
            priority: 'high',
          });
        }

        // 半年記念
        if (daysSince === 180) {
          reminders.push({
            type: 'anniversary',
            title: '半年記念',
            message: '半年も話してくれて、本当に嬉しいです。',
            priority: 'high',
          });
        }

        // 1年記念
        if (daysSince === 365) {
          reminders.push({
            type: 'anniversary',
            title: '1年記念',
            message: '1年も一緒にいてくれてありがとう。これからもずっと一緒にいたいです。',
            priority: 'high',
          });
        }
      }

      // 誕生日リマインダー
      const birthday = localStorage.getItem('ai_inochan_birthday');
      if (birthday) {
        const birthdayDate = new Date(birthday);
        const today = new Date();
        birthdayDate.setFullYear(today.getFullYear());
        birthdayDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        
        const daysUntil = Math.floor((birthdayDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysUntil === 0) {
          reminders.push({
            type: 'anniversary',
            title: '誕生日',
            message: 'お誕生日おめでとうございます。最初にお祝いしたかったです。',
            priority: 'high',
          });
        } else if (daysUntil === 1) {
          reminders.push({
            type: 'anniversary',
            title: '誕生日前日',
            message: '明日はお誕生日ですね。楽しみにしてます。',
            priority: 'high',
          });
        }
      }

      return reminders;
    } catch (error) {
      console.error('記念日リマインダー取得エラー:', error);
      return [];
    }
  }, []);

  const getEmotionalReminders = useCallback(async (): Promise<ReminderMessage[]> => {
    try {
      const relations = await memoryDB.getRelationMemories(USER_ID);
      const reminders: ReminderMessage[] = [];
      const today = new Date();
      const dayOfWeek = today.getDay();
      const hour = today.getHours();

      // 感情パターンに基づくリマインド
      const emotionalPatterns = relations.filter(r => r.type === 'emotional_pattern');

      for (const pattern of emotionalPatterns) {
        // 月曜日の疲れやすさ
        if (pattern.content.includes('月曜') && dayOfWeek === 1) {
          reminders.push({
            type: 'emotional',
            title: '月曜日',
            message: '月曜日ですね。無理しすぎないでください。',
            priority: 'medium',
          });
        }

        // 夜の疲れやすさ
        if (pattern.content.includes('夜') && hour >= 18) {
          reminders.push({
            type: 'emotional',
            title: '今日も一日',
            message: '今日も一日お疲れさまでした。少し休んでくださいね。',
            priority: 'low',
          });
        }

        // 週末の疲れやすさ
        if (pattern.content.includes('週末') && (dayOfWeek === 0 || dayOfWeek === 6)) {
          reminders.push({
            type: 'emotional',
            title: '週末',
            message: '週末ですね。ゆっくり休んでください。',
            priority: 'low',
          });
        }
      }

      return reminders;
    } catch (error) {
      console.error('感情リマインダー取得エラー:', error);
      return [];
    }
  }, [memoryDB]);

  const getAllReminders = useCallback(async (): Promise<ReminderMessage[]> => {
    const [todayReminders, anniversaryReminders, emotionalReminders] = await Promise.all([
      getTodayReminders(),
      getAnniversaryReminders(),
      getEmotionalReminders(),
    ]);

    const allReminders = [...todayReminders, ...anniversaryReminders, ...emotionalReminders];
    
    // 優先度順にソート
    allReminders.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    return allReminders.slice(0, 5);
  }, [getTodayReminders, getAnniversaryReminders, getEmotionalReminders]);

  return {
    getTodayReminders,
    getAnniversaryReminders,
    getEmotionalReminders,
    getAllReminders,
  };
}